import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const IDLE_MESSAGES = [
  "What shall we learn today? 🌟",
  "Every question makes you smarter! 🧠",
];

// Tiger face built from layered CSS elements
const TigerFace = ({ state, size }) => {
  const dim = size === "sm" ? 32 : 96;
  const isThinking = state === "thinking";

  if (size === "sm") {
    return (
      <div
        style={{ width: dim, height: dim, fontSize: dim * 0.75 }}
        className="flex items-center justify-center select-none"
      >
        🐯
      </div>
    );
  }

  return (
    <div
      style={{ width: dim, height: dim }}
      className="relative flex items-center justify-center select-none"
    >
      {/* Main tiger face */}
      <div
        className="relative flex items-center justify-center rounded-full"
        style={{
          width: dim,
          height: dim,
          background: "#448AFF",
          boxShadow: "none",
        }}
      >
        {/* Ears */}
        <div
          className="absolute -top-3 left-3 w-7 h-7 rounded-full"
          style={{ background: "#FF6D00" }}
        />
        <div
          className="absolute -top-3 right-3 w-7 h-7 rounded-full"
          style={{ background: "#FF6D00" }}
        />
        {/* Ear inner */}
        <div
          className="absolute -top-1.5 left-4 w-4 h-4 rounded-full"
          style={{ background: "#FFB300" }}
        />
        <div
          className="absolute -top-1.5 right-4 w-4 h-4 rounded-full"
          style={{ background: "#FFB300" }}
        />

        {/* Forehead stripes */}
        <div
          className="absolute top-2 left-1/2 -translate-x-1/2 flex gap-1"
        >
          <div className="w-0.5 h-3 rounded-full" style={{ background: "rgba(0,0,0,0.25)", transform: "rotate(-10deg)" }} />
          <div className="w-0.5 h-4 rounded-full" style={{ background: "rgba(0,0,0,0.25)" }} />
          <div className="w-0.5 h-3 rounded-full" style={{ background: "rgba(0,0,0,0.25)", transform: "rotate(10deg)" }} />
        </div>

        {/* Eyes container — thinking animation applied here */}
        <div
          className={`absolute flex gap-4 ${isThinking ? "sheru-think" : ""}`}
          style={{ top: "35%", left: "50%", transform: "translateX(-50%)" }}
        >
          {/* Left eye */}
          <div className="flex items-center justify-center rounded-full bg-white" style={{ width: 18, height: 18 }}>
            <div className="rounded-full bg-[#1A1A1A]" style={{ width: 10, height: 10 }} />
            <div className="absolute w-2 h-2 rounded-full bg-white opacity-60" style={{ marginTop: -6, marginLeft: 4 }} />
          </div>
          {/* Right eye */}
          <div className="flex items-center justify-center rounded-full bg-white" style={{ width: 18, height: 18 }}>
            <div className="rounded-full bg-[#1A1A1A]" style={{ width: 10, height: 10 }} />
          </div>
        </div>

        {/* Nose */}
        <div
          className="absolute rounded-full"
          style={{
            width: 14,
            height: 10,
            background: "#1A1A1A",
            top: "55%",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        />

        {/* Mouth */}
        <div
          className="absolute"
          style={{
            top: "68%",
            left: "50%",
            transform: "translateX(-50%)",
            width: 20,
            height: 8,
            borderBottom: "3px solid rgba(0,0,0,0.4)",
            borderLeft: "3px solid rgba(0,0,0,0.4)",
            borderRight: "3px solid rgba(0,0,0,0.4)",
            borderRadius: "0 0 10px 10px",
          }}
        />

        {/* Cheek whisker dots */}
        <div className="absolute flex gap-1" style={{ top: "58%", left: "8%" }}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="w-1 h-1 rounded-full" style={{ background: "rgba(0,0,0,0.3)" }} />
          ))}
        </div>
        <div className="absolute flex gap-1" style={{ top: "58%", right: "8%" }}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="w-1 h-1 rounded-full" style={{ background: "rgba(0,0,0,0.3)" }} />
          ))}
        </div>

        {/* Chin stripe */}
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full"
          style={{ width: 20, height: 12, background: "#FFE0B2" }}
        />
      </div>
    </div>
  );
};

export const Sheru = ({ state = "idle", message, size = "lg" }) => {
  const [idleMessageIdx, setIdleMessageIdx] = useState(0);
  const [showBubble, setShowBubble] = useState(true);

  // Auto-alternate idle messages every 4s
  useEffect(() => {
    if (state !== "idle" || message) return;
    const interval = setInterval(() => {
      setShowBubble(false);
      setTimeout(() => {
        setIdleMessageIdx((prev) => (prev + 1) % IDLE_MESSAGES.length);
        setShowBubble(true);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, [state, message]);

  // Reset bubble when external message changes
  useEffect(() => {
    setShowBubble(false);
    const t = setTimeout(() => setShowBubble(true), 100);
    return () => clearTimeout(t);
  }, [message]);

  const displayMessage = message || IDLE_MESSAGES[idleMessageIdx];

  const animClass =
    state === "thinking" ? "sheru-think" :
    state === "celebrating" ? "sheru-celebrate" :
    "sheru-breathe";

  if (size === "sm") {
    return (
      <div className="flex items-center gap-1.5">
        <TigerFace state={state} size="sm" />
        {displayMessage && (
          <span className="text-xs text-white/80 font-body">{displayMessage}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1 py-2">
      {/* Reserve height for speech bubble so tiger face never jumps */}
      <div className="min-h-[80px] flex items-end justify-center w-full">
      <AnimatePresence mode="wait">
        {showBubble && displayMessage && (
          <motion.div
            key={displayMessage}
            initial={{ opacity: 0, scale: 0.85, y: 4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 4 }}
            transition={{ duration: 0.25 }}
            className="relative px-4 py-2 rounded-2xl bg-white dark:bg-[#1A1A24] border border-border shadow-sm max-w-[220px] text-center"
          >
            <p className="text-sm font-heading font-semibold text-foreground leading-snug">
              {displayMessage}
            </p>
            {/* Bubble tail */}
            <div
              className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0"
              style={{
                borderLeft: "8px solid transparent",
                borderRight: "8px solid transparent",
                borderTop: "8px solid hsl(var(--border))",
              }}
            />
            <div
              className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-0 h-0"
              style={{
                borderLeft: "7px solid transparent",
                borderRight: "7px solid transparent",
                borderTop: "7px solid white",
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
      </div>

      {/* Tiger face with animation */}
      <div className={animClass}>
        <TigerFace state={state} size="lg" />
      </div>

      {/* Label */}
      <p className="text-[10px] font-heading font-semibold text-muted-foreground tracking-widest uppercase">
        Sheru 🐯
      </p>
    </div>
  );
};
